import type { AtomsColorToken, AtomsPrimitiveColorToken } from '@opendoor/atoms/utils';

/**
 * Any color a `var(--color-*)` reference can resolve to: the semantic `od-*`
 * tokens plus the raw Opendoor primitives (`blue-500`, `white`, …). Prefer a
 * semantic token where one exists; reach for a primitive only when no semantic
 * role fits (e.g. a fixed brand paint on an SVG).
 */
type AtomsAnyColorToken = AtomsColorToken | AtomsPrimitiveColorToken;

/**
 * Resolve an atoms color token to its CSS `var(...)` reference. Use only where a
 * color must reach the DOM as a value — e.g. an icon `color` prop or an SVG `fill` —
 * rather than as a Tailwind className (prefer `text-od-*` / `bg-od-*` classes otherwise).
 * The reference is theme-aware: it resolves against the active theme at render time.
 *
 * The token set is sourced from `@opendoor/atoms` (`AtomsColorToken` /
 * `AtomsPrimitiveColorToken`), so it stays in sync with the design system
 * instead of being duplicated here.
 *
 * Atoms replacement for bricks-next `getCSSVariable` / `getTokenValue(token, 'color')`.
 */
export const getCssVariable = (token: AtomsAnyColorToken): `var(--color-${AtomsAnyColorToken})` =>
  `var(--color-${token})`;
